import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSchemas } from '../api/hooks';
import { apiFetch } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import SchemaStatusDialog from '../components/SchemaStatusDialog';

/**
 * A DID shown shortened, with the full value one click away.
 *
 * Truncation alone left users unable to obtain the full author DID — the
 * title tooltip is not selectable and disappears on mobile. The button copies
 * the whole string and confirms briefly.
 */
function DidWithCopy({ did, bold = false }: { did: string; bold?: boolean }) {
  const [copied, setCopied] = useState(false);
  if (!did) return <span className="text-xs text-text-faint">—</span>;
  const copy = () => {
    navigator.clipboard.writeText(did);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`font-mono text-xs text-text-main ${bold ? 'font-bold' : ''}`} title={did}>
        {truncateDid(did)}
      </span>
      <button
        type="button"
        onClick={copy}
        className="text-text-faint transition-colors hover:text-primary"
        aria-label={`Copy ${did}`}
        title={copied ? 'Copied' : 'Copy full DID'}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 14 }}>
          {copied ? 'check' : 'content_copy'}
        </span>
      </button>
    </span>
  );
}

function truncateDid(did: string, chars = 16) {
  if (!did || did.length <= chars + 6) return did;
  return did.slice(0, chars) + '...' + did.slice(-4);
}

export default function Schemas() {
  const { data: schemas, loading, error, refetch } = useSchemas();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['schemas', 'common']);

  // Admins manage schemas from this page, so the deactivate control lives here
  // too — not only on the issuer's own SchemaManagement page.
  type SchemaRow = NonNullable<typeof schemas>[number];
  const [statusTarget, setStatusTarget] = useState<SchemaRow | null>(null);
  // Shown after a deactivate-and-revoke, which changes more than the table shows.
  const [statusNotice, setStatusNotice] = useState('');


  const selected = (Array.isArray(schemas) ? schemas : []).find((s) => s.id === selectedId) ?? null;

  return (
    <div className="space-y-8">
      {statusNotice && (
        <div className="flex items-center justify-between rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <span>{statusNotice}</span>
          <button onClick={() => setStatusNotice('')} className="text-emerald-600 hover:text-emerald-800" aria-label="Dismiss">
            <span className="material-symbols-outlined" style={{ fontSize: 16 }}>close</span>
          </button>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('list.title')}</h1>
          <p className="mt-1 text-sm text-text-muted">{t('list.subtitle')}</p>
          {/* Say which scope this page is, so it is not confused with the
              issuer portal's "My Schemas" (which lists only your own). */}
          <p className="mt-0.5 text-xs text-text-faint">
            Schemas from every issuer on the network.
            {!hasRole('admin') && ' Your own are under Issuer Portal → My Schemas.'}
          </p>
        </div>
        {hasRole('issuer') && !hasRole('admin') && (
          <button className="btn-primary" onClick={() => navigate('/issuer/schemas')}>
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>add_circle</span>
            {t('list.registerSchema')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="text-center py-20 text-red-500">{t('common:errors.loadSchemasFailed')}</div>
      ) : (Array.isArray(schemas) ? schemas : []).length === 0 ? (
        <div className="text-center py-20 text-text-faint">{t('common:empty.noSchemas')}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              { label: t('list.stats.total'), value: String((Array.isArray(schemas) ? schemas : []).length) },
              { label: t('list.stats.active'), value: String((Array.isArray(schemas) ? schemas : []).filter((s) => s.status === 'active').length) },
              { label: t('list.stats.authors'), value: String(new Set((Array.isArray(schemas) ? schemas : []).map((s) => s.author ?? s.authorName)).size) },
            ].map((stat) => (
              <div key={stat.label} className="stat-card">
                <span className="text-[10px] font-bold uppercase tracking-wider text-text-muted">{stat.label}</span>
                <div className="mt-2 flex items-baseline gap-2">
                  <span className="text-3xl font-bold text-text-main">{stat.value}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="overflow-hidden rounded-xl border border-border-subtle bg-white shadow-sm">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border-subtle bg-slate-50/50">
                      <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('list.table.schema')}</th>
                      <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('list.table.author')}</th>
                      <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('list.table.version')}</th>
                      <th className="px-6 py-3 text-left text-[11px] font-extrabold uppercase tracking-widest text-text-faint">{t('list.table.status')}</th>
                      {hasRole('admin') && (
                        <th className="px-6 py-3 text-right text-[11px] font-extrabold uppercase tracking-widest text-text-faint">Actions</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(schemas) ? schemas : []).map((schema) => (
                      <tr
                        key={schema.id}
                        onClick={() => setSelectedId(schema.id)}
                        className={`cursor-pointer border-b border-border-subtle transition-colors hover:bg-slate-50/60 ${
                          selectedId === schema.id ? 'bg-blue-50/50' : ''
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="flex size-10 items-center justify-center rounded-lg bg-blue-50">
                              <span className="material-symbols-outlined text-primary" style={{ fontSize: 20 }}>schema</span>
                            </div>
                            <div>
                              <p className="text-sm font-bold tracking-tight text-text-main">{schema.name}</p>
                              <p className="text-[10px] font-bold text-text-faint">v{schema.version}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {/* Issuer organisation from onboarding, with the DID
                              underneath — the network view spans every issuer. */}
                          {schema.authorName && (
                            <p className="text-sm font-bold text-text-main">{schema.authorName}</p>
                          )}
                          <DidWithCopy did={schema.author ?? ''} />
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm font-bold text-text-main">v{schema.version}</span>
                        </td>
                        <td className="px-6 py-4">
                          {schema.status === 'active' ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100/50 bg-emerald-50 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700">
                              <span className="size-1.5 rounded-full bg-emerald-500"></span>
                              {t('common:status.active')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                              <span className="size-1.5 rounded-full bg-slate-400"></span>
                              {t('common:status.draft')}
                            </span>
                          )}
                        </td>
                        {hasRole('admin') && (
                          <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                            <button
                              onClick={() => setStatusTarget(schema)}
                              title={schema.deactivated ? 'Reactivate schema' : 'Deactivate schema (blocks new issuance)'}
                              className="rounded-lg px-2.5 py-1 text-xs font-bold text-text-faint transition-colors hover:bg-slate-100 hover:text-text-main"
                            >
                              {schema.deactivated ? 'Reactivate' : 'Deactivate'}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="lg:col-span-1">
              {selected ? (
                <div className="rounded-xl border border-border-subtle bg-white p-6 shadow-sm">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="flex size-10 items-center justify-center rounded-lg bg-blue-50">
                      <span className="material-symbols-outlined text-primary" style={{ fontSize: 20 }}>schema</span>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-text-main">{selected.name}</p>
                      <p className="text-[10px] font-bold text-text-faint">v{selected.version}</p>
                    </div>
                  </div>

                  <div className="mb-4 space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-text-faint">{t('list.detail.author')}</span>
                      <DidWithCopy did={selected.author ?? ''} bold />
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-faint">{t('list.detail.version')}</span>
                      <span className="font-bold text-text-main">v{selected.version}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-text-faint">{t('list.detail.updated')}</span>
                      <span className="font-bold text-text-main">{selected.updated ?? selected.createdAt ?? ''}</span>
                    </div>
                  </div>

                  <div className="border-t border-border-subtle pt-4">
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('list.detail.fields')}</span>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(selected.fields ?? []).map((field) => (
                        <span key={field.name} className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">
                          {field.name}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="mt-4 border-t border-border-subtle pt-4">
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-text-faint">{t('list.detail.schemaId')}</span>
                    <div className="mt-1">
                      <span className="inline-block rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 font-mono text-xs font-semibold text-text-main">
                        {selected.id}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex h-64 items-center justify-center rounded-xl border border-border-subtle bg-white p-6 text-center shadow-sm">
                  <div>
                    <span className="material-symbols-outlined mb-2 text-text-faint" style={{ fontSize: 40 }}>schema</span>
                    <p className="text-sm text-text-faint">{t('list.detail.selectHint')}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <SchemaStatusDialog

        target={statusTarget ? { ...statusTarget, credentialCount: statusTarget.credentialCount ?? 0 } : null}

        onClose={() => setStatusTarget(null)}

        onDone={(msg) => { setStatusTarget(null); setStatusNotice(msg ?? ''); refetch(); }}

      />
    </div>
  );
}
